import { useRef, useState, useEffect } from 'react';
import '../styles/admin.css';

// Целевое соотношение сторон фото каталога
export const CATALOG_RATIO = 4 / 3;
export const RATIO_TOLERANCE = 0.05; // 5% — допуск

const HANDLE_PX = 10; // размер угловых хэндлов
const MIN_CROP  = 60; // минимальная ширина кропа

// Редактор фото (кроп під 4:3, піпетка + пензлик щоб замалювати зайве, вільне
// обертання) — спільний для "Додати товар" (AdminPage) і "Замінити фото"
// (ProductModal). Винесено в окремий файл, щоб не дублювати ~330 рядків.
export const CropModal = ({ file, onConfirm, onCancel }) => {
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const dragRef   = useRef(null);
  const paintRef  = useRef(null);
  const lastPt    = useRef(null);
  const [sel, setSel]               = useState(null);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [cursor, setCursor]         = useState('crosshair');
  const [rotation, setRotation]     = useState(0);
  const [tool,      setTool]        = useState('crop');
  const [color,     setColor]       = useState('#ffffff');
  const [brushSize, setBrushSize]   = useState(20);
  const [pan,       setPan]         = useState({ x: 0, y: 0 });
  // Рамка кропу (sel) завжди прив'язана до ШИРИНИ і не може перевищити ширину
  // canvas — для витягнутого вгору фото (портретна орієнтація) це означає, що
  // рамка 4:3 фізично не може захопити всю висоту кадру, скільки її не тягни.
  // Єдиний вихід — зменшити саме фото під рамкою, тому додаємо зум (окремо
  // від розміру самої рамки).
  const [scale,     setScale]       = useState(1);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const maxW = Math.min(window.innerWidth - 64, 860);
      const maxH = window.innerHeight - 220;
      let cw = img.width, ch = img.height;
      if (cw > maxW) { ch = Math.round(ch * maxW / cw); cw = maxW; }
      if (ch > maxH) { cw = Math.round(cw * maxH / ch); ch = maxH; }
      setCanvasSize({ w: cw, h: ch });
      let sw = cw, sh = Math.round(cw / CATALOG_RATIO);
      if (sh > ch) { sh = ch; sw = Math.round(ch * CATALOG_RATIO); }
      setSel({ x: Math.round((cw - sw) / 2), y: Math.round((ch - sh) / 2), w: sw, h: sh });
    };
    const objUrl = URL.createObjectURL(file);
    img.src = objUrl;
    return () => URL.revokeObjectURL(objUrl);
  }, [file]);

  useEffect(() => {
    if (!canvasSize.w) return;
    const pc = document.createElement('canvas');
    pc.width = canvasSize.w; pc.height = canvasSize.h;
    paintRef.current = pc;
  }, [canvasSize]);

  useEffect(() => {
    if (!imgRef.current || !canvasSize.w || !canvasRef.current) return;
    const c = canvasRef.current;
    c.width = canvasSize.w; c.height = canvasSize.h;
    const ctx = c.getContext('2d');
    const bgBlurR = 18;
    const bgPad   = bgBlurR * 4;
    const bgTmp   = document.createElement('canvas');
    bgTmp.width   = canvasSize.w + bgPad * 2;
    bgTmp.height  = canvasSize.h + bgPad * 2;
    const bgX     = bgTmp.getContext('2d');
    bgX.filter    = `blur(${bgBlurR}px)`;
    bgX.drawImage(imgRef.current, bgPad - canvasSize.w * 0.08, bgPad - canvasSize.h * 0.08, canvasSize.w * 1.16, canvasSize.h * 1.16);
    bgX.filter    = 'none';
    ctx.drawImage(bgTmp, bgPad, bgPad, canvasSize.w, canvasSize.h, 0, 0, canvasSize.w, canvasSize.h);
    ctx.save();
    ctx.translate(canvasSize.w / 2 + pan.x, canvasSize.h / 2 + pan.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.drawImage(imgRef.current, -canvasSize.w / 2, -canvasSize.h / 2, canvasSize.w, canvasSize.h);
    ctx.restore();
    if (paintRef.current) ctx.drawImage(paintRef.current, 0, 0);
  }, [canvasSize, rotation, pan, scale]); // eslint-disable-line

  // Нажатие на ручку вращения — запускает rotate-drag
  const onRotateHandleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width  / 2;
    const cy = rect.top  + rect.height / 2;
    dragRef.current = {
      mode: 'rotate',
      startRotation: rotation,
      startAngle: Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI,
      cx, cy,
    };
  };

  const handleRotate = () => setRotation(r => (Math.round(r / 90) * 90 + 90) % 360);

  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  // Определяем какой хэндл под курсором (или null)
  const hitHandle = (p, s) => {
    if (!s) return null;
    const ht = HANDLE_PX + 4; // зона клика чуть больше визуала
    const corners = { nw:[s.x, s.y], ne:[s.x+s.w, s.y], sw:[s.x, s.y+s.h], se:[s.x+s.w, s.y+s.h] };
    for (const [id, [cx, cy]] of Object.entries(corners)) {
      if (Math.abs(p.x - cx) <= ht && Math.abs(p.y - cy) <= ht) return id;
    }
    return null;
  };

  const inSel = (p, s) => s && p.x > s.x+HANDLE_PX && p.x < s.x+s.w-HANDLE_PX
                              && p.y > s.y+HANDLE_PX && p.y < s.y+s.h-HANDLE_PX;

  // Зажим в границы canvas с учётом соотношения
  const clampSel = ({ x, y, w, h }) => {
    w = Math.max(MIN_CROP, w);
    h = Math.round(w / CATALOG_RATIO);
    const { w: cw, h: ch } = canvasSize;
    x = Math.max(0, Math.min(cw - w, x));
    y = Math.max(0, Math.min(ch - h, y));
    // если после зажима X/Y размер вылез
    if (x + w > cw) { w = cw - x; h = Math.round(w / CATALOG_RATIO); }
    if (y + h > ch) { h = ch - y; w = Math.round(h * CATALOG_RATIO); x = Math.max(0, Math.min(cw - w, x)); }
    return { x, y, w, h };
  };

  const paintAt = (from, to) => {
    const pc = paintRef.current;
    if (!pc) return;
    const pCtx = pc.getContext('2d');
    pCtx.strokeStyle = color; pCtx.fillStyle = color;
    pCtx.lineWidth = brushSize; pCtx.lineCap = 'round'; pCtx.lineJoin = 'round';
    pCtx.beginPath(); pCtx.moveTo(from.x, from.y); pCtx.lineTo(to.x, to.y); pCtx.stroke();
    pCtx.beginPath(); pCtx.arc(to.x, to.y, brushSize / 2, 0, Math.PI * 2); pCtx.fill();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = color; ctx.fillStyle = color;
    ctx.lineWidth = brushSize; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
    ctx.beginPath(); ctx.arc(to.x, to.y, brushSize / 2, 0, Math.PI * 2); ctx.fill();
  };

  const onMouseDown = (e) => {
    if (!canvasRef.current) return;
    e.preventDefault();
    const p = getPos(e);

    if (tool === 'eyedropper') {
      const d = canvasRef.current.getContext('2d').getImageData(Math.round(p.x), Math.round(p.y), 1, 1).data;
      setColor('#' + [d[0], d[1], d[2]].map(v => v.toString(16).padStart(2, '0')).join(''));
      setTool('brush');
      return;
    }
    if (tool === 'brush') {
      lastPt.current = p;
      paintAt(p, p);
      return;
    }
    const handle = hitHandle(p, sel);
    if (handle) {
      dragRef.current = { mode: 'resize', handle, startX: p.x, startY: p.y, initSel: { ...sel } };
    } else if (inSel(p, sel)) {
      dragRef.current = { mode: 'move', startX: p.x, startY: p.y, initSel: { ...sel } };
    } else {
      dragRef.current = { mode: 'pan', startX: p.x, startY: p.y, initPan: { ...pan } };
    }
  };

  const onMouseMove = (e) => {
    const d = dragRef.current;

    if (d?.mode === 'rotate') {
      e.preventDefault();
      const angle = Math.atan2(e.clientY - d.cy, e.clientX - d.cx) * 180 / Math.PI;
      let delta = angle - d.startAngle;
      if (delta > 180)  delta -= 360;
      if (delta < -180) delta += 360;
      setRotation(d.startRotation + delta);
      return;
    }

    if (d?.mode === 'pan') {
      e.preventDefault();
      const p = getPos(e);
      setPan({ x: d.initPan.x + (p.x - d.startX), y: d.initPan.y + (p.y - d.startY) });
      return;
    }

    if (tool === 'brush' && lastPt.current) {
      e.preventDefault();
      const p = getPos(e);
      paintAt(lastPt.current, p);
      lastPt.current = p;
      return;
    }

    if (!canvasRef.current) return;
    const p = getPos(e);

    if (!d) {
      if (tool === 'eyedropper') { setCursor('crosshair'); return; }
      if (tool === 'brush')      { setCursor('cell');       return; }
      const handle = hitHandle(p, sel);
      if (handle === 'nw' || handle === 'se') setCursor('nwse-resize');
      else if (handle === 'ne' || handle === 'sw') setCursor('nesw-resize');
      else if (inSel(p, sel)) setCursor('move');
      else setCursor('grab');
      return;
    }

    e.preventDefault();

    if (d.mode === 'move') {
      setSel(clampSel({ x: d.initSel.x + (p.x - d.startX), y: d.initSel.y + (p.y - d.startY), w: d.initSel.w, h: d.initSel.h }));
    } else if (d.mode === 'resize') {
      const s = d.initSel;
      const h = d.handle;
      const anchorX = h.includes('w') ? s.x + s.w : s.x;
      const anchorY = h.startsWith('n') ? s.y + s.h : s.y;
      let w = Math.max(MIN_CROP, Math.abs(p.x - anchorX));
      let nh = Math.round(w / CATALOG_RATIO);
      const nx = h.includes('e') ? anchorX : anchorX - w;
      const ny = h.startsWith('s') ? anchorY : anchorY - nh;
      setSel(clampSel({ x: nx, y: ny, w, h: nh }));
    }
  };

  const onMouseUp = () => { dragRef.current = null; lastPt.current = null; };

  const onWheel = (e) => {
    if (tool !== 'crop') return;   // прокрутка не заважає малюванню/піпетці
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1.06 : 1 / 1.06;
    setScale(s => Math.min(3, Math.max(0.15, s * delta)));
  };

  const handleConfirm = () => {
    if (!sel || !imgRef.current) return;
    const img = imgRef.current;
    const scaleX = img.width  / canvasSize.w;
    const scaleY = img.height / canvasSize.h;

    // Повёрнутый canvas в полном разрешении оригинала
    const rotCanvas = document.createElement('canvas');
    rotCanvas.width  = img.width;
    rotCanvas.height = img.height;
    const rCtx = rotCanvas.getContext('2d');
    // Фон — размытая версия (через временный canvas с отступом > 3×sigma)
    const expBlurR = 28;
    const expPad   = expBlurR * 4;
    const expTmp   = document.createElement('canvas');
    expTmp.width   = rotCanvas.width  + expPad * 2;
    expTmp.height  = rotCanvas.height + expPad * 2;
    const expX     = expTmp.getContext('2d');
    expX.filter    = `blur(${expBlurR}px)`;
    expX.drawImage(img, expPad - img.width * 0.08, expPad - img.height * 0.08, img.width * 1.16, img.height * 1.16);
    expX.filter    = 'none';
    rCtx.drawImage(expTmp, expPad, expPad, rotCanvas.width, rotCanvas.height, 0, 0, rotCanvas.width, rotCanvas.height);
    rCtx.save();
    rCtx.translate(img.width / 2 + pan.x * scaleX, img.height / 2 + pan.y * scaleY);
    rCtx.rotate((rotation * Math.PI) / 180);
    rCtx.scale(scale, scale);
    rCtx.drawImage(img, -img.width / 2, -img.height / 2);
    rCtx.restore();

    if (paintRef.current) {
      const sp = document.createElement('canvas');
      sp.width = img.width; sp.height = img.height;
      sp.getContext('2d').drawImage(paintRef.current, 0, 0, img.width, img.height);
      rCtx.drawImage(sp, 0, 0);
    }

    // Вырезаем выделенную область в полном разрешении
    const out = document.createElement('canvas');
    out.width  = Math.round(sel.w * scaleX);
    out.height = Math.round(sel.h * scaleY);
    out.getContext('2d').drawImage(rotCanvas,
      sel.x * scaleX, sel.y * scaleY, out.width, out.height,
      0, 0, out.width, out.height);
    out.toBlob(blob => onConfirm(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', 0.92);
  };

  const handles = sel
    ? [ { id:'nw', x:sel.x,       y:sel.y,       cur:'nwse-resize' },
        { id:'ne', x:sel.x+sel.w, y:sel.y,       cur:'nesw-resize' },
        { id:'sw', x:sel.x,       y:sel.y+sel.h, cur:'nesw-resize' },
        { id:'se', x:sel.x+sel.w, y:sel.y+sel.h, cur:'nwse-resize' } ]
    : [];

  return (
    <div className="crop-modal-overlay"
         onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>
      <div className="crop-modal-title">Виберіть область (4:3)</div>
      <div className="crop-modal-hint">
        {tool === 'crop'       && 'Перетягніть рамку · поза рамкою — переміщення фото · колесо миші або 🔍 — зум'}
        {tool === 'eyedropper' && 'Клікніть на колір фону'}
        {tool === 'brush'      && 'Малюйте поверх зайвих елементів'}
      </div>

      <div className="crop-toolbar">
        <button className={`crop-btn crop-tool-btn${tool === 'crop'       ? ' crop-btn--active' : ''}`} onClick={() => setTool('crop')}>✂ Кроп</button>
        <button className={`crop-btn crop-tool-btn${tool === 'eyedropper' ? ' crop-btn--active' : ''}`} onClick={() => setTool('eyedropper')}>⊕ Піпетка</button>
        <button className={`crop-btn crop-tool-btn${tool === 'brush'      ? ' crop-btn--active' : ''}`} onClick={() => setTool('brush')}>✏ Пензлик</button>
        <div className="rt-editor-size-wrap" title="Зум фото — зменшіть, якщо кросівок не влазить у рамку">
          <span className="rt-editor-size-label">🔍</span>
          <input type="range" min={0.15} max={3} step={0.01} value={scale} onChange={e => setScale(Number(e.target.value))} />
          <span className="rt-editor-size-label">{Math.round(scale * 100)}%</span>
        </div>
        {(tool === 'brush' || tool === 'eyedropper') && (
          <>
            <div className="rt-editor-color-wrap" title="Колір">
              <div className="rt-editor-color-swatch" style={{ background: color }} />
              <input type="color" value={color} onChange={e => { setColor(e.target.value); setTool('brush'); }} className="rt-editor-color-input" />
            </div>
            <div className="rt-editor-size-wrap">
              <input type="range" min={5} max={150} value={brushSize} onChange={e => setBrushSize(Number(e.target.value))} />
              <span className="rt-editor-size-label">{brushSize}px</span>
            </div>
          </>
        )}
      </div>

      <div className="crop-canvas-wrap"
           style={{ width: canvasSize.w, height: canvasSize.h,
                    cursor: tool === 'eyedropper' ? 'crosshair' : tool === 'brush' ? 'cell' : cursor }}
           onMouseDown={onMouseDown} onWheel={onWheel}>
        <canvas ref={canvasRef} />
        {sel && (
          <>
            <div className="crop-selection" style={{ left:sel.x, top:sel.y, width:sel.w, height:sel.h }} />
            {handles.map(h => (
              <div key={h.id} className="crop-handle"
                   style={{ left: h.x - HANDLE_PX/2, top: h.y - HANDLE_PX/2, cursor: h.cur }} />
            ))}
          </>
        )}
      </div>
      <div className="crop-rotate-area">
        <div
          className="crop-rotate-handle"
          onMouseDown={onRotateHandleMouseDown}
          title="Тяните для свободного вращения"
        >↻</div>
        <span className="crop-rotate-angle">
          {(() => { const a = ((rotation % 360) + 360) % 360; return a > 180 ? `−${360 - Math.round(a)}°` : `${Math.round(a)}°`; })()}
        </span>
        <button className="crop-btn crop-btn--rotate crop-btn--snap" onClick={handleRotate} title="Snap +90°">
          90°
        </button>
      </div>
      <div className="crop-modal-actions">
        <button className="crop-btn crop-btn--cancel" onClick={onCancel}>Отмена</button>
        <button className="crop-btn crop-btn--confirm" onClick={handleConfirm} disabled={!sel}>
          Обрезать и загрузить
        </button>
      </div>
    </div>
  );
};

export default CropModal;
